import { BLOCKS } from '@contentful/rich-text-types';
import type { CallToAction } from '@snapchat/mw-contentful-schema';
import { BackgroundColor, MessageContext } from '@snapchat/snap-design-system-marketing';
import type { FC, ReactNode } from 'react';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { logError } from '../../helpers/logging';
import { useContentfulQuery } from '../../hooks/useContentfulQuery';
import type { Items } from '../../types/Items';
import type { RichText } from '../../types/RichText';
import type { BlockDataProps } from '../Block';
import type { SubNavigationDataProps } from '../SubNavigation';
import type { HeroDataProps } from '.';
import { Hero } from './Hero';
import type { BlockWithTitle, LatestPostHeroProps } from './latestPostQuery';
import { latestPostHeroQuery } from './latestPostQuery';

export const LatestPostHero: FC<{
  backgroundColor?: BackgroundColor;
  postChildren?: ReactNode;
}> = ({ backgroundColor, postChildren }) => {
  const { data } = useContentfulQuery<LatestPostHeroProps>(latestPostHeroQuery);
  const { formatMessage } = useContext(MessageContext);
  const { pathname } = useLocation();
  const latestPostCtaTitle = formatMessage({ id: 'viewArticle', defaultMessage: 'View Article' });

  const titleCtaRichText: RichText = {
    json: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [],
              value: latestPostCtaTitle,
              nodeType: 'text',
            },
          ],
          nodeType: BLOCKS.PARAGRAPH,
        },
      ],
      nodeType: BLOCKS.DOCUMENT,
    },
    links: undefined,
  };

  const slugData = data?.slugCollection.items[0];

  const slug = slugData?.slug;
  let blockHero: HeroDataProps | undefined;
  let blocksCollection:
    | Items<BlockDataProps | HeroDataProps | SubNavigationDataProps | BlockWithTitle>
    | undefined;

  switch (slugData?.page.__typename) {
    case 'Page':
      blocksCollection = slugData.page.blocksCollection;

      blockHero = blocksCollection.items.find(
        (item): item is HeroDataProps => item.__typename === 'BlockHero'
      );
      break;

    case 'PageAsset':
      blockHero = {
        __typename: 'BlockHero',
        sys: { id: 'asset_block_hero' },
        title: slugData?.page?.media?.title,
        body: slugData?.page?.media?.description,
      };
      break;

    case 'Experiment': {
      blocksCollection = (
        slugData.page.defaultReference as unknown as {
          blocksCollection: Items<
            BlockDataProps | HeroDataProps | SubNavigationDataProps | BlockWithTitle
          >;
        }
      ).blocksCollection;

      blockHero = blocksCollection.items.find(
        (item): item is HeroDataProps => item.__typename === 'BlockHero'
      );
      break;
    }
  }

  // If no BlockHero found, find the first block with a populated title
  if (!blockHero && blocksCollection) {
    const blockWithTitle = blocksCollection.items.find((item): item is BlockWithTitle =>
      Boolean(item && 'title' in item && item.title)
    );

    if (blockWithTitle) {
      logError({
        component: 'LatestPostHero',
        message: `Latest post "${slug}" does not have a hero. Using fallback title from ${blockWithTitle.__typename}`,
      });

      blockHero = {
        __typename: 'BlockHero',
        sys: { id: `fallback_from_${blockWithTitle.__typename}` },
        title: blockWithTitle.title as RichText | string,
        backgroundColor: BackgroundColor.Black,
      };
    }
  }

  const callsToActionCollection: Items<CallToAction> = {
    items: [
      {
        _id: 'generated',
        sys: {
          id: 'generated',
        },
        url: slug,
        analytics: {
          _id: 'generated',
          sys: {
            id: 'generated',
          },
          label: `Latest Post Hero CTA: ${pathname}`,
        },
        presentation: {
          __typename: 'Button',
          _id: 'generated',
          sys: {
            id: 'generated',
          },
          url: slug,
          title: titleCtaRichText,
          buttonType: 'Primary',
        },
      },
    ],
  };

  if (!blockHero) return null;

  const heroBackgroundColor = blockHero.backgroundColor ?? backgroundColor ?? BackgroundColor.White;

  backgroundColor ??= BackgroundColor.White;
  return (
    <Hero
      {...blockHero}
      callsToActionCollection={callsToActionCollection}
      backgroundColor={heroBackgroundColor}
      theme={backgroundColor}
      postChildren={postChildren}
    />
  );
};
